import moment from 'moment';

import { required } from '../form-validation';

const dateFormat = 'ddd, MMM DD';
const minutesInHour = 60;
const durationOptions = [
    { id: 1, label: '1 Hour' },
    { id: 1.5, label: '1.5 Hours' },
    { id: 2, label: '2 Hours' },
    { id: 2.5, label: '2.5 Hours' },
    { id: 3, label: '3 Hours' },
];

const permissionTypes = [
    { id: 1, label: 'Searchable by Users' }, // public
    { id: 2, label: 'Not Searchable by Users' }, // private
];

const filterPlayers = (players, playerId) => {
    return players.filter(player => player.source.id !== playerId);
};

const findPlayer = (players, playerId) => {
    return players.find(player => player.source.id === playerId);
};

const getFreePlayButtonTextForState = (isEditing, isSaving) => {
    if (isEditing && !isSaving) {
        return 'Update';
    }
    if (isSaving || (isSaving && isEditing)) {
        return 'Saving...';
    }
    return 'Create & Share';
};

const getPaidHitButtonTextForState = (isEditing, isSaving, isDuplicating) => {
    if (isDuplicating && !isSaving) {
        return 'Save';
    }
    if (isDuplicating && isSaving) {
        return 'Saving...';
    }
    if (isEditing && !isSaving) {
        return 'Update';
    }
    if (isSaving && !isEditing) {
        return 'Creating...';
    }
    if (isSaving && isEditing) {
        return 'Updating...';
    }
    return 'Create Session';
};

const getEventEndDate = (startTime, duration) => {
    const durationTotalMinutes = duration * minutesInHour;
    const currentStartTime = moment(startTime);
    const endDate = moment(currentStartTime).add(durationTotalMinutes, 'minutes');
    return endDate.toISOString();
};

const getNewMemberInvites = (previousInvites, currentInvites) => {
    const inviteIds = previousInvites.map(invitee => invitee.source.memberId);
    return currentInvites.reduce((invites, player) => {
        if (!inviteIds.includes(player.memberId)) {
            invites.push(player);
        }
        return invites;
    }, []);
};

const getNewEmailInvites = (previousInvites, currentInvites) => {
    const inviteEmails = previousInvites.map(invitee => invitee.email);
    return currentInvites.reduce((invites, player) => {
        if (!inviteEmails.includes(player.email)) {
            invites.push(player);
        }
        return invites;
    }, []);
};

const getFreePlayPopupContentForState = (isEditing) => {
    const title = isEditing ? 'You\'ve successfully updated your event!' : 'You\'ve successfully created your event.';
    const subtitle = '';

    return { title, subtitle };
};

const getPaidHitPopupContentForState = (isEditing) => {
    const title = isEditing ? 'You\'ve successfully updated your session!' : 'Congrats on creating your session!';
    const subtitle = isEditing ? '' : 'We\'ll notify you when someone signs up.';

    return { title, subtitle };
};

const sortPlayersByRating = (players) => {
    return players.sort((playerA, playerB) => playerB.source.singlesUtr - playerA.source.singlesUtr);
};

const mapOrganizer = (user) => {
    return {
        source: {
            id: user?.id,
            displayName: `${user?.firstName} ${user?.lastName}`,
            location: user?.location,
            playerProfileImages: user?.claimedPlayer.playerProfileImages,
            gender: user?.gender,
            singlesUtr: user?.claimedPlayer?.ratingSingles,
            doublesUtr: user?.claimedPlayer?.ratingDoubles,
            myUtrSingles: user?.claimedPlayer?.myUtrSingles,
            myUtrDoubles: user?.claimedPlayer?.myUtrDoubles,
            singlesUtrDisplay: user?.claimedPlayer?.singlesUtrDisplay,
            doublesUtrDisplay: user?.claimedPlayer?.doublesUtrDisplay,
            myUtrSinglesDisplay: user?.claimedPlayer?.myUtrSinglesDisplay,
            myUtrDoublesDisplay: user?.claimedPlayer?.myUtrDoublesDisplay,
            ratingStatusSingles: user?.claimedPlayer?.ratingStatusSingles,
            ratingStatusDoubles: user?.claimedPlayer?.ratingStatusDoubles,
            myUtrStatusSingles: user?.claimedPlayer?.myUtrStatusSingles,
            myUtrStatusDoubles: user?.claimedPlayer?.myUtrStatusDoubles,
            pbrRatingDisplay: user?.claimedPlayer?.pbrRatingDisplay,
            historicRatings: user?.historicRatings,
            singlesPkbRatingDisplay: user?.claimedPlayer?.singlesPkbRatingDisplay,
            doublesPkbRatingDisplay: user?.claimedPlayer?.doublesPkbRatingDisplay,
            utrRange: user?.utrRange,
        },
    };
};

const mapPlayer = (player) => {
    let playerLocation;
    if (typeof player?.location === 'string') {
        playerLocation = { display: player?.location };
    } else {
        playerLocation = player.location;
    }
    return {
        source: {
            id: player.id,
            memberId: player.memberId,
            displayName: `${player.firstName} ${player.lastName}`,
            location: playerLocation,
            playerProfileImages: player.playerProfileImages,
            gender: player?.gender,
            singlesUtr: player?.singlesUtr,
            doublesUtr: player?.doublesUtr,
            myUtrSingles: player?.myUtrSingles,
            myUtrDoubles: player?.myUtrDoubles,
            singlesUtrDisplay: player?.singlesUtrDisplay,
            doublesUtrDisplay: player?.doublesUtrDisplay,
            myUtrSinglesDisplay: player?.myUtrSinglesDisplay,
            myUtrDoublesDisplay: player?.myUtrDoublesDisplay,
            ratingStatusSingles: player?.ratingStatusSingles,
            ratingStatusDoubles: player?.ratingStatusDoubles,
            myUtrStatusSingles: player?.myUtrStatusSingles,
            myUtrStatusDoubles: player?.myUtrStatusDoubles,
            pbrRatingDisplay: player?.pbrRatingDisplay,
            historicRatings: player?.historicRatings,
            utrRange: player?.utrRange,
        },
    };
};

const mapMembers = (members) => {
    const mappedMembers = members?.map(member => {
        let memberLocation;
        if (typeof member?.location === 'string') {
            memberLocation = { display: member?.location };
        } else {
            memberLocation = member.location;
        }
        return {
            source: {
                id: member.playerId || member.id,
                memberId: member.memberId,
                displayName: `${member.firstName} ${member.lastName}`,
                location: memberLocation,
                playerProfileImages: member.playerProfileImages,
                gender: member?.gender,
                singlesUtr: member?.singlesUtr,
                doublesUtr: member?.doublesUtr,
                myUtrSingles: member?.myUtrSingles,
                myUtrDoubles: member?.myUtrDoubles,
                singlesUtrDisplay: member?.singlesUtrDisplay,
                doublesUtrDisplay: member?.doublesUtrDisplay,
                myUtrSinglesDisplay: member?.myUtrSinglesDisplay,
                myUtrDoublesDisplay: member?.myUtrDoublesDisplay,
                ratingStatusSingles: member?.ratingStatusSingles,
                ratingStatusDoubles: member?.ratingStatusDoubles,
                myUtrStatusSingles: member?.myUtrStatusSingles,
                myUtrStatusDoubles: member?.myUtrStatusDoubles,
                pbrRatingDisplay: member?.pbrRatingDisplay,
                historicRatings: member?.historicRatings,
                utrRange: member?.utrRange,
            },
        };
    });
    return sortPlayersByRating(mappedMembers);
};

const mapEmailInvites = (members) => {
    const mappedMembers = members.map(member => { return { email: member.email }; });
    return mappedMembers;
};

const validateDate = (value) => {
    const isPastDate = moment(value).isSameOrBefore(moment());
    if (value && isPastDate) {
        return { error: 'Event date cannot be in the past.' };
    }
    return required(value);
};

const getIdForType = (types, value) => {
    const foundType = types.find(type => type.value === value);
    return foundType.id;
};

export {
    dateFormat,
    durationOptions,
    filterPlayers,
    findPlayer,
    getFreePlayButtonTextForState,
    getPaidHitButtonTextForState,
    getEventEndDate,
    getNewEmailInvites,
    getNewMemberInvites,
    getFreePlayPopupContentForState,
    getPaidHitPopupContentForState,
    mapEmailInvites,
    mapPlayer,
    mapMembers,
    permissionTypes,
    sortPlayersByRating,
    validateDate,
    getIdForType,
    mapOrganizer,
};
